import { useCallback } from 'react';

import { css } from '@emotion/react';
import {
  Badge,
  comparePrimarySubtextsSearchMatch,
  menuItemContentClasses,
  menuListClasses,
  scorePrimarySubtextsForItem,
} from '@kc-console/common-ui';
import {
  EXTERNAL_LINK,
  QUERY_KEY,
  TOKEN_SCOPE,
  useScopedLinkBuilder,
  useServiceAppState,
} from '@kc-console/context';
import { formatValueOrDefault, useTranslation } from '@kc-console/core';

import { useRefinedProjectMenuState } from '../../../hooks';
import GnbSelect from './GnbSelect';

import type { SelectItemOption, SelectOption, SelectProps } from '@kc-console/common-ui';

const NO_MATCHING_PROJECT_VALUE = '__no_matching_project__';

const getProjectOptionText = (option: SelectItemOption<string>) => option.text ?? '';

const getProjectOptionSubTexts = (option: SelectItemOption<string>) => option.subTexts;

const ProjectMenu = () => {
  const { t } = useTranslation();
  const { getProps } = useScopedLinkBuilder();
  const { currentProject, accessibleProjects } = useRefinedProjectMenuState();
  const {
    state: { defaultProjectId, scope, serviceKeyword },
  } = useServiceAppState();

  const { id: projectId = '', name: projectName = '' } = currentProject || {};
  const isNotProjectScope = scope !== TOKEN_SCOPE.PROJECT;
  const tooltipMessage = isNotProjectScope
    ? t('layout:gnb.project.not_supported')
    : t('layout:gnb.project.current_name');
  const projectSettingsTitle = t('layout:gnb.project.settings');
  const projectSelectTitle = t('layout:gnb.project.select');

  const options: SelectOption<string>[] = [
    {
      type: 'section',
      title: projectSettingsTitle,
      items: [{ text: t('layout:gnb.project.management'), value: '' }],
    },
    {
      title: projectSelectTitle,
      type: 'section',
      items: accessibleProjects?.map(project => {
        const { name, id } = project;
        return {
          text: formatValueOrDefault({ origin: name }),
          subTexts: [formatValueOrDefault({ origin: id })],
          truncateSubTextIndex: 0,
          startBadge:
            defaultProjectId === id ? (
              <Badge variant="tint" label={t('layout:gnb.project.default')} color="gray" />
            ) : undefined,
          value: id || '',
        };
      }),
    },
  ];

  const filterProjectOptions = useCallback<NonNullable<SelectProps<string>['filterOptions']>>(
    (options, { searchInputValue = '' }) => {
      if (!searchInputValue.trim()) return options;

      return options.map(option => {
        if (option.type !== 'section' || option.title !== projectSelectTitle) {
          return option;
        }

        const filteredProjectOptions = option.items
          .filter(projectOption =>
            Number.isFinite(
              scorePrimarySubtextsForItem(
                projectOption,
                searchInputValue,
                getProjectOptionText,
                getProjectOptionSubTexts,
              ),
            ),
          )
          .sort((a, b) =>
            comparePrimarySubtextsSearchMatch(
              a,
              b,
              searchInputValue,
              getProjectOptionText,
              getProjectOptionSubTexts,
              { preserveStableOrderWhenSearchMetaTies: true },
            ),
          );

        return {
          ...option,
          items: filteredProjectOptions.length
            ? filteredProjectOptions
            : [
                {
                  disabled: true,
                  text: t('common:common.list.no_matching_items'),
                  value: NO_MATCHING_PROJECT_VALUE,
                },
              ],
        };
      });
    },
    [projectSelectTitle, t],
  );

  const handleProjectChange = (projectId: unknown) => {
    if (projectId === '') {
      // TODO(navigation): Select anchor 지원 시 isAnchor:false 제거 → href spread
      getProps(EXTERNAL_LINK.PROJECT_MANAGEMENT.MEMBERS, {
        external: true,
        isAnchor: false,
      }).onClick();
      return;
    }

    if (typeof projectId === 'string') {
      // TODO(navigation): Select anchor 지원 시 isAnchor:false 제거 → href spread
      getProps(serviceKeyword, {
        forceFullPageReload: true,
        isAnchor: false,
        searchParams: {
          [QUERY_KEY.PROJECT_ID]: projectId,
          ...(currentProject !== projectId ? { change_project: 'true' } : undefined),
        },
      }).onClick();
    }
  };

  return (
    <div css={{ width: '100%', overflow: 'auto' }}>
      <GnbSelect
        block
        aria-label={projectSelectTitle}
        options={options}
        searchable
        filterOptions={filterProjectOptions}
        value={projectId}
        renderTooltip={() => tooltipMessage}
        renderValue={() => projectName || '-'}
        placeholder={t('layout:gnb.project.no_info')}
        onChange={handleProjectChange}
        maxHeight={706}
        slotProps={{
          dropdownMenu: {
            slotProps: {
              menuContainer: {
                css: css`
                  .${menuListClasses.root} {
                    scrollbar-color: rgba(193, 199, 208, 0.5) transparent;
                  }
                  .${menuItemContentClasses.subTextFrame} {
                    padding-block: 0px;
                  }
                `,
              },
            },
          },
        }}
        autoWidth={300}
      />
    </div>
  );
};

export default ProjectMenu;
